import { motion } from "framer-motion";
import { Toaster } from "sonner";
import { ArrowRight, Phone, Shield, MapPin, Star, BadgeCheck } from "lucide-react";
import SEO from "@/components/SEO";
import CommercialWrapsForm from "@/components/CommercialWrapsForm";

const LOGO = "https://customer-assets.emergentagent.com/job_dave-wraps-studio/artifacts/rzh2dyxf_cropped-928_Logo-281x300.png";
// Self-hosted wrap-shop cover footage (storefront → lettering → print), H.264 720p faststart
const COVER_VIDEO = "/videos/wraps-cover.mp4";
const COVER_POSTER = "/images/wraps-cover-poster.jpg";

/**
 * Meta Ads landing page — /lp/commercial-wraps
 * Minimal header (no site nav), noindex, not linked from the site.
 * Leads tagged "Meta Ads — Commercial Wraps LP"; analytics lp key "commercial-wraps".
 */
export default function CommercialWrapsLP() {
  const scrollToForm = () => {
    document.getElementById("lpc-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="page-lp-commercial-wraps" className="min-h-screen bg-[var(--bg-2)]">
      <SEO
        title="FREE Custom Wrap Mockup — See Your Brand on Your Vehicle | 928 Wraps, Prescott Valley"
        description="Request a free custom mockup from 928 Wraps in Prescott Valley, AZ — your logo, your colors, on your actual vehicle, before you spend a dollar. Owner-operated commercial wrap shop since 2012."
        path="/lp/commercial-wraps"
        noindex
      />
      <Toaster richColors position="top-right" />

      {/* Minimal top bar */}
      <header className="sticky top-0 inset-x-0 z-40 bg-white/85 backdrop-blur-xl border-b border-neutral-200">
        <div className="wrap flex items-center justify-between h-[64px]">
          <div className="flex items-center gap-2.5">
            <img src={LOGO} alt="928 Wraps" className="h-8 w-auto" />
            <span className="hidden sm:inline-block font-mono text-[10px] tracking-[0.3em] pl-2.5 border-l border-neutral-200 text-neutral-500">PRESCOTT VALLEY · AZ</span>
          </div>
          <a
            href="tel:+19282730784"
            data-testid="lpc-phone"
            data-track="phone_click"
            data-track-location="lp-commercial-wraps-nav"
            className="flex items-center gap-2 text-sm font-semibold text-neutral-800 hover:text-black"
          >
            <Phone size={16} className="text-[var(--red)]" />
            (928) 273-0784
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="pt-6 md:pt-20 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-[500px] h-[500px] rounded-full bg-red-500/5 blur-3xl"></div>
          <div className="absolute -bottom-20 -left-20 w-[500px] h-[500px] rounded-full bg-amber-400/10 blur-3xl"></div>
        </div>
        <div className="wrap grid md:grid-cols-12 gap-10 relative">
          {/* On mobile: form appears FIRST (above the fold). On desktop: hero copy left, form right. */}
          <div className="md:col-span-7 order-2 md:order-1">
            <div className="eyebrow eyebrow-red">Commercial Vehicle Wraps · Prescott, AZ</div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="h-display text-5xl md:text-6xl uppercase mt-4 leading-[0.95]"
            >
              Thousands see your<br/>
              vehicle.<br/>
              <span className="text-[var(--red)]">What do they see?</span>
            </motion.h1>
            <p className="mt-6 text-lg text-neutral-600 max-w-xl leading-relaxed">
              Tell us about your vehicle and Dave designs a real mockup — <strong className="text-neutral-900">your logo, your colors, on your actual truck</strong> — completely free. No obligation, no pushy calls. Just proof it&apos;ll look incredible before you commit a dollar.
            </p>

            {/* Trust ribbon */}
            <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-neutral-500">
              <div className="flex items-center gap-2">
                <Star size={16} className="text-amber-500 fill-amber-500" />
                <span>5.0 on Google · Real reviews</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield size={16} className="text-[var(--red)]" />
                <span>Design, print & install under one roof</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-neutral-700" />
                <span>Local · Owner-operated · Since 2012</span>
              </div>
              <div className="flex items-center gap-2" data-testid="lpc-trust-jobs">
                <BadgeCheck size={16} className="text-[var(--red)]" />
                <span>4,000+ jobs completed</span>
              </div>
            </div>

            {/* Cover hero footage — mobile only, under the copy. aspect reserved = no layout shift */}
            <video
              data-testid="lpc-hero-video"
              className="mt-8 w-full aspect-[4/5] object-cover rounded-3xl border border-neutral-200 bg-neutral-900 md:hidden"
              src={COVER_VIDEO}
              poster={COVER_POSTER}
              autoPlay
              muted
              loop
              playsInline
            />

            <div className="mt-10 hidden md:flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <button
                onClick={scrollToForm}
                data-testid="lpc-hero-cta"
                data-track="lp_cta_click"
                data-track-location="lp-commercial-wraps-hero"
                className="btn-red text-base"
              >
                Get My FREE Mockup <ArrowRight size={16}/>
              </button>
              <a
                href="tel:+19282730784"
                data-testid="lpc-hero-phone"
                data-track="phone_click"
                data-track-location="lp-commercial-wraps-hero"
                className="btn-ghost text-base"
              >
                <Phone size={16}/> (928) 273-0784
              </a>
            </div>
          </div>

          {/* Inline form — mobile: above the fold (order-1). Desktop: right side (order-2). */}
          <div id="lpc-form" className="md:col-span-5 order-1 md:order-2 scroll-mt-24">
            {/* The Meta ads offer — statement text (not a button) pinned above the form */}
            <div data-testid="lpc-offer-banner" className="mb-4 flex items-center justify-center gap-3">
              <span className="hidden sm:block h-px w-8 bg-[var(--red)]/40" />
              <span className="text-[13px] sm:text-sm font-bold tracking-wide text-[var(--red)] whitespace-nowrap">
                Claim your FREE Custom Mockup
              </span>
              <span className="hidden sm:block h-px w-8 bg-[var(--red)]/40" />
            </div>
            <CommercialWrapsForm source="Meta Ads — Commercial Wraps LP" lpKey="commercial-wraps" testidPrefix="lpc" />
          </div>
        </div>
      </section>
    </div>
  );
}
